<template>
  <v-container fluid class="club-events pa-3 pa-md-6">
    <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between mb-6">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 elevation-2">
          <v-icon dark>mdi-calendar-star</v-icon>
        </v-avatar>
        <div>
          <h1 class="text-h5 text-md-h4 font-weight-bold mb-1">{{ $t('ce_title') }}</h1>
          <p class="grey--text text--darken-1 mb-0">{{ $t('ce_description') }}</p>
        </div>
      </div>
      <div class="mt-4 mt-sm-0">
        <v-btn color="primary" outlined rounded class="mr-2" :loading="loading" @click="loadEvents">
          <v-icon left>mdi-refresh</v-icon>{{ $t('refresh') }}
        </v-btn>
        <v-btn color="primary" rounded @click="openForm()">
          <v-icon left>mdi-plus</v-icon>{{ $t('ce_new') }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="errorMessage" type="error" outlined dismissible @input="errorMessage = ''">{{ errorMessage }}</v-alert>

    <v-alert v-if="pendingCount" type="warning" outlined class="mb-4">
      <div class="d-flex align-center flex-wrap">
        <span><b>{{ pendingCount }}</b> {{ $t('ce_pending_banner') }}</span>
        <v-spacer />
        <v-btn small color="warning" depressed @click="showPending">{{ $t('ce_review') }}</v-btn>
      </div>
    </v-alert>

    <v-card outlined class="mb-4">
      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-text-field v-model="filters.date_from" type="date" :label="$t('ce_date_from')" dense outlined hide-details @change="loadEvents" />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="filters.date_to" type="date" :label="$t('ce_date_to')" dense outlined hide-details @change="loadEvents" />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select v-model="filters.approval_status" :items="approvalFilterItems" :label="$t('ce_approval')" dense outlined hide-details @change="loadEvents" />
          </v-col>
          <v-col v-if="hasCost" cols="12" sm="3">
            <v-select v-model="filters.payment_status" :items="paymentFilterItems" :label="$t('ce_payment')" dense outlined hide-details @change="loadEvents" />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" :label="$t('ce_search')" dense outlined hide-details clearable />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined>
      <v-data-table :headers="headers" :items="events" :search="search" :loading="loading" :footer-props="footerProps" item-key="id">
        <template v-slot:item.date="{ item }">{{ item.date }} · {{ item.time }}</template>
        <template v-slot:item.guests="{ item }">{{ item.guests_inside }} / {{ item.guests_total }}</template>
        <template v-slot:item.price="{ item }">{{ formatPrice(item.price) }}</template>
        <template v-slot:item.payment_status="{ item }">
          <v-chip small :color="paymentColor(item.payment_status)" dark>{{ paymentLabel(item.payment_status) }}</v-chip>
        </template>
        <template v-slot:item.canceled="{ item }">
          <v-chip small :color="statusInfo(item).color" dark>{{ statusInfo(item).label }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <template v-if="isReviewable(item)">
            <v-btn icon small color="green" :title="$t('ce_approve')" @click="openApprove(item)"><v-icon>mdi-check-circle</v-icon></v-btn>
            <v-btn icon small color="red" :title="$t('ce_reject')" @click="openReject(item)"><v-icon>mdi-close-circle</v-icon></v-btn>
          </template>
          <v-btn icon small :title="$t('ce_manage')" @click="openDetail(item)"><v-icon>mdi-account-multiple</v-icon></v-btn>
          <v-btn icon small :disabled="item.canceled || item.approval_status === 'rejected'" :title="$t('ce_edit')" @click="openForm(item)"><v-icon>mdi-pencil</v-icon></v-btn>
        </template>
        <template v-slot:no-data>{{ $t('ce_empty') }}</template>
      </v-data-table>
    </v-card>

    <!-- Crear / editar -->
    <v-dialog v-model="formDialog" max-width="640" persistent>
      <v-card>
        <v-card-title>{{ editingId ? $t('ce_edit') : $t('ce_new') }}</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-text-field v-model="form.name" :label="$t('ce_name')" :rules="[required]" outlined dense />
            <v-row dense>
              <v-col cols="6"><v-text-field v-model="form.date" type="date" :label="$t('ce_date')" :rules="[required]" outlined dense /></v-col>
              <v-col cols="6"><v-text-field v-model="form.time" type="time" :label="$t('ce_time')" :rules="[required]" outlined dense /></v-col>
            </v-row>
            <v-text-field v-model="form.location" :label="$t('ce_location')" :rules="[required]" outlined dense />
            <v-autocomplete
              v-model="form.host"
              :items="members"
              :loading="loadingMembers"
              item-text="label"
              item-value="id"
              :label="$t('ce_host')"
              :rules="[required]"
              :no-data-text="$t('ce_no_members')"
              outlined
              dense
            />
            <v-text-field v-if="hasCost" v-model="form.price" type="number" min="0" step="0.01" prefix="$" :label="$t('ce_price')" :rules="[required]" outlined dense />
            <v-textarea v-model="form.description" :label="$t('ce_description_field')" rows="2" outlined dense />
            <v-text-field v-model="form.notes" :label="$t('ce_notes')" counter="300" outlined dense />
            <v-combobox
              v-if="!editingId"
              v-model="form.guests"
              :label="$t('ce_guests')"
              :hint="$t('ce_guests_hint')"
              persistent-hint
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              append-icon=""
              outlined
              dense
            >
              <template v-slot:selection="{ attrs, item, parent, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  small
                  close
                  @click:close="parent.selectItem(item)"
                >
                  {{ item }}
                </v-chip>
              </template>
            </v-combobox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="formDialog = false">{{ $t('ce_close') }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveEvent">{{ $t('ce_save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detalle: invitados, pago, QR -->
    <v-dialog v-model="detailDialog" max-width="720" scrollable>
      <v-card v-if="detail">
        <v-card-title class="d-flex justify-space-between">
          <span>{{ detail.name }}</span>
          <v-chip small :color="statusInfo(detail).color" dark>{{ statusInfo(detail).label }}</v-chip>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="detail.approval_status === 'pending' && !detail.canceled" type="info" dense text class="mb-3">
            {{ $t('ce_requested_by_member') }}
          </v-alert>
          <v-alert v-if="detail.approval_status === 'rejected'" type="error" dense text class="mb-3">
            <b>{{ $t('ce_rejection_reason') }}:</b> {{ detail.rejection_reason || '-' }}
          </v-alert>
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6"><b>{{ $t('ce_host') }}:</b> {{ detail.host_name || '-' }}</v-col>
            <v-col cols="12" sm="6"><b>{{ $t('ce_date') }}:</b> {{ detail.date }} {{ detail.time }}</v-col>
            <v-col cols="12" sm="6"><b>{{ $t('ce_location') }}:</b> {{ detail.location }}</v-col>
            <v-col v-if="detail.qr_code" cols="12" sm="6"><b>QR:</b> <code>{{ detail.qr_code }}</code></v-col>
            <v-col v-if="hasCost && detail.payment_status !== 'not_applicable'" cols="12">
              <b>{{ $t('ce_payment') }}:</b> {{ formatPrice(detail.price) }}
              <v-chip small class="ml-2" :color="paymentColor(detail.payment_status)" dark>{{ paymentLabel(detail.payment_status) }}</v-chip>
              <v-btn small text color="primary" :loading="paying" @click="togglePayment">
                {{ detail.payment_status === 'paid' ? $t('ce_mark_pending') : $t('ce_mark_paid') }}
              </v-btn>
            </v-col>
          </v-row>

          <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('ce_guests') }} ({{ detail.guests.length }})</div>
          <v-list dense>
            <v-list-item v-for="g in detail.guests" :key="g.id">
              <v-list-item-content>
                <v-list-item-title>{{ g.full_name }}</v-list-item-title>
                <v-list-item-subtitle>{{ guestStatusLabel(g.status) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="g.status === 0 && !detail.canceled && detail.approval_status !== 'rejected'" class="flex-row">
                <v-btn icon small @click="renameGuest(g)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="red" @click="removeGuest(g)"><v-icon small>mdi-delete</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="!detail.guests.length"><v-list-item-content>{{ $t('ce_no_guests') }}</v-list-item-content></v-list-item>
          </v-list>

          <v-row v-if="!detail.canceled && detail.approval_status !== 'rejected'" dense class="mt-2">
            <v-col cols="12" sm="9"><v-text-field v-model="newGuest" :label="$t('ce_add_guest')" dense outlined hide-details @keyup.enter="addGuest" /></v-col>
            <v-col cols="12" sm="3"><v-btn block color="primary" :loading="addingGuest" :disabled="!newGuest.trim()" @click="addGuest">{{ $t('ce_add') }}</v-btn></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!detail.canceled && detail.approval_status !== 'rejected'" text color="red" @click="cancelDialog = true">{{ $t('ce_cancel_event') }}</v-btn>
          <v-spacer />
          <template v-if="isReviewable(detail)">
            <v-btn text color="red" @click="openReject(detail)">{{ $t('ce_reject') }}</v-btn>
            <v-btn color="green" dark depressed @click="openApprove(detail)">{{ $t('ce_approve') }}</v-btn>
          </template>
          <v-btn text @click="detailDialog = false">{{ $t('ce_close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="420">
      <v-card>
        <v-card-title>{{ $t('ce_cancel_event') }}</v-card-title>
        <v-card-text>{{ $t('ce_cancel_confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDialog = false">{{ $t('ce_close') }}</v-btn>
          <v-btn color="red" dark :loading="canceling" @click="confirmCancel">{{ $t('ce_cancel_event') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Aprobar solicitud -->
    <v-dialog v-model="approveDialog" max-width="440" persistent>
      <v-card v-if="reviewing">
        <v-card-title>{{ $t('ce_approve_title') }}</v-card-title>
        <v-card-text>
          <p class="font-weight-bold mb-1">{{ reviewing.name }}</p>
          <p>{{ $t('ce_approve_confirm') }}</p>
          <v-text-field
            v-if="hasCost"
            v-model="approvePrice"
            type="number"
            min="0"
            step="0.01"
            prefix="$"
            :label="$t('ce_price')"
            :hint="$t('ce_approve_price_hint')"
            persistent-hint
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="reviewingBusy" @click="approveDialog = false">{{ $t('ce_close') }}</v-btn>
          <v-btn color="green" dark depressed :loading="reviewingBusy" :disabled="hasCost && approvePrice === ''" @click="confirmApprove">{{ $t('ce_approve') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rechazar solicitud -->
    <v-dialog v-model="rejectDialog" max-width="440" persistent>
      <v-card v-if="reviewing">
        <v-card-title>{{ $t('ce_reject_title') }}</v-card-title>
        <v-card-text>
          <p class="font-weight-bold mb-1">{{ reviewing.name }}</p>
          <p>{{ $t('ce_reject_confirm') }}</p>
          <v-textarea v-model="rejectReason" :label="$t('ce_reject_reason')" counter="300" rows="2" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="reviewingBusy" @click="rejectDialog = false">{{ $t('ce_close') }}</v-btn>
          <v-btn color="red" dark depressed :loading="reviewingBusy" :disabled="rejectReason.length > 300" @click="confirmReject">{{ $t('ce_reject') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">{{ snackbarMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters } from 'vuex'
import {
  getClubEvents, getClubEvent, createClubEvent, updateClubEvent, cancelClubEvent, setClubEventPayment,
  approveClubEvent, rejectClubEvent, addClubEventGuests, renameClubEventGuest, deleteClubEventGuest
} from '@/services/clubEvents'

const emptyForm = () => ({
  name: '', date: '', time: '', location: '', host: null, price: '', description: '', notes: '', guests: []
})

export default {
  name: 'ClubEvents',
  data: () => ({
    events: [],
    members: [],
    loading: false,
    loadingMembers: false,
    errorMessage: '',
    search: '',
    filters: { date_from: '', date_to: '', payment_status: '', approval_status: '' },
    pendingCount: 0,
    reviewing: null,
    approveDialog: false,
    rejectDialog: false,
    approvePrice: '',
    rejectReason: '',
    reviewingBusy: false,
    footerProps: { 'items-per-page-options': [10, 25, 50, 100] },
    formDialog: false,
    editingId: null,
    form: emptyForm(),
    saving: false,
    detailDialog: false,
    detail: null,
    newGuest: '',
    addingGuest: false,
    paying: false,
    cancelDialog: false,
    canceling: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success'
  }),
  computed: {
    ...mapGetters(['getProfile']),
    hasCost () {
      return !!(this.getProfile && this.getProfile.events_have_cost)
    },
    headers () {
      const headers = [
        { text: this.$t('ce_name'), value: 'name' },
        { text: this.$t('ce_date'), value: 'date' },
        { text: this.$t('ce_host'), value: 'host_name' },
        { text: this.$t('ce_location'), value: 'location' },
        { text: this.$t('ce_inside'), value: 'guests', sortable: false }
      ]
      if (this.hasCost) {
        headers.push({ text: this.$t('ce_price'), value: 'price' }, { text: this.$t('ce_payment'), value: 'payment_status' })
      }
      headers.push(
        { text: this.$t('status'), value: 'canceled' },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'end' }
      )
      return headers
    },
    approvalFilterItems () {
      return [
        { text: this.$t('ce_all'), value: '' },
        { text: this.$t('ce_status_pending'), value: 'pending' },
        { text: this.$t('ce_status_approved'), value: 'approved' },
        { text: this.$t('ce_status_rejected'), value: 'rejected' }
      ]
    },
    paymentFilterItems () {
      return [
        { text: this.$t('ce_all'), value: '' },
        { text: this.$t('ce_payment_pending'), value: 'pending' },
        { text: this.$t('ce_payment_paid'), value: 'paid' }
      ]
    }
  },
  created () {
    this.loadEvents()
    this.loadMembers()
  },
  methods: {
    required (v) {
      return (v !== null && v !== undefined && String(v).trim() !== '') || this.$t('ce_required')
    },
    apiError (error) {
      const data = error && error.response && error.response.data
      if (!data) return this.$t('ce_error')
      const value = data.error || data.detail || data
      if (typeof value === 'string') return value
      return Object.entries(value).map(([k, v]) => `${k}: ${[].concat(v).join(', ')}`).join(' · ')
    },
    notify (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    formatPrice (price) {
      return price === null || price === undefined ? '-' : `$${Number(price).toFixed(2)}`
    },
    statusInfo (event) {
      if (event.approval_status === 'rejected') return { color: 'red', label: this.$t('ce_status_rejected') }
      if (event.canceled) {
        return event.approval_status === 'pending'
          ? { color: 'grey', label: this.$t('ce_status_withdrawn') }
          : { color: 'red', label: this.$t('ce_canceled') }
      }
      if (event.approval_status === 'pending') return { color: 'orange', label: this.$t('ce_status_pending') }
      return { color: 'green', label: this.$t('ce_active') }
    },
    isReviewable (event) {
      return event.approval_status === 'pending' && !event.canceled
    },
    async loadPendingCount () {
      try {
        const response = await getClubEvents({ approval_status: 'pending', canceled: 'false' })
        this.pendingCount = response.data.length
      } catch (error) {
        this.pendingCount = 0
      }
    },
    showPending () {
      this.filters.approval_status = 'pending'
      this.loadEvents()
    },
    openApprove (event) {
      this.reviewing = event
      this.approvePrice = event.price === null || event.price === undefined ? '' : String(event.price)
      this.approveDialog = true
    },
    openReject (event) {
      this.reviewing = event
      this.rejectReason = ''
      this.rejectDialog = true
    },
    async finishReview (message) {
      this.approveDialog = false
      this.rejectDialog = false
      this.notify(message)
      if (this.detailDialog && this.detail && this.reviewing && this.detail.id === this.reviewing.id) {
        await this.refreshDetail()
      } else {
        this.loadEvents()
      }
    },
    async confirmApprove () {
      this.reviewingBusy = true
      try {
        const data = this.hasCost ? { price: this.approvePrice } : {}
        await approveClubEvent(this.reviewing.id, data)
        await this.finishReview(this.$t('ce_approved_ok'))
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.reviewingBusy = false
      }
    },
    async confirmReject () {
      this.reviewingBusy = true
      try {
        await rejectClubEvent(this.reviewing.id, { reason: this.rejectReason.trim() })
        await this.finishReview(this.$t('ce_rejected_ok'))
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.reviewingBusy = false
      }
    },
    paymentColor (status) {
      return { paid: 'green', pending: 'orange', not_applicable: 'grey' }[status] || 'grey'
    },
    paymentLabel (status) {
      return this.$t({ paid: 'ce_payment_paid', pending: 'ce_payment_pending', not_applicable: 'ce_payment_free' }[status])
    },
    guestStatusLabel (status) {
      return this.$t(['ce_guest_pending', 'ce_guest_inside', 'ce_guest_exited'][status])
    },
    async loadEvents () {
      this.loading = true
      this.errorMessage = ''
      const params = {}
      Object.keys(this.filters).forEach(k => { if (this.filters[k]) params[k] = this.filters[k] })
      try {
        const response = await getClubEvents(params)
        this.events = response.data
        this.loadPendingCount()
      } catch (error) {
        this.errorMessage = this.apiError(error)
      } finally {
        this.loading = false
      }
    },
    async loadMembers () {
      this.loadingMembers = true
      try {
        const response = await axios({
          method: 'get',
          url: (process.env.VUE_APP_API_URL || 'https://clubtest.intrare.app') + '/principal_resident/v2/',
          headers: { 'authorization': 'Token ' + store.getters.getToken }
        })
        this.members = (response.data.principalResident || []).map(r => ({
          id: r.idUser.id,
          label: `${r.fullname} (${r.street || ''} ${r.numberHouse || ''})`.trim()
        }))
      } catch (error) {
        this.errorMessage = this.apiError(error)
      } finally {
        this.loadingMembers = false
      }
    },
    openForm (item) {
      this.editingId = item ? item.id : null
      this.form = item ? this.formFromEvent(item) : emptyForm()
      this.formDialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    formFromEvent (item) {
      return Object.assign(emptyForm(), {
        name: item.name,
        date: item.date,
        time: item.time,
        location: item.location,
        host: item.host,
        price: item.price === null ? '' : item.price,
        description: item.description || '',
        notes: item.notes || ''
      })
    },
    async saveEvent () {
      if (!this.$refs.form.validate()) return
      const payload = {
        name: this.form.name,
        date: this.form.date,
        time: this.form.time,
        location: this.form.location,
        host: this.form.host,
        description: this.form.description,
        notes: this.form.notes || null
      }
      if (this.hasCost) payload.price = this.form.price
      if (!this.editingId) {
        const uniqueGuests = []
        const guestNames = new Set()
        this.form.guests.forEach(guest => {
          const name = String(guest).trim()
          const normalizedName = name.toLocaleLowerCase()
          if (name && !guestNames.has(normalizedName)) {
            guestNames.add(normalizedName)
            uniqueGuests.push({ full_name: name })
          }
        })
        payload.guests = uniqueGuests
      }
      this.saving = true
      try {
        if (this.editingId) await updateClubEvent(this.editingId, payload)
        else await createClubEvent(payload)
        this.formDialog = false
        this.notify(this.$t('ce_saved'))
        this.loadEvents()
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.saving = false
      }
    },
    async openDetail (item) {
      try {
        const response = await getClubEvent(item.id)
        this.detail = response.data
        this.newGuest = ''
        this.detailDialog = true
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      }
    },
    async refreshDetail () {
      const response = await getClubEvent(this.detail.id)
      this.detail = response.data
      this.loadEvents()
    },
    async addGuest () {
      const name = this.newGuest.trim()
      if (!name) return
      this.addingGuest = true
      try {
        await addClubEventGuests(this.detail.id, [{ full_name: name }])
        this.newGuest = ''
        await this.refreshDetail()
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.addingGuest = false
      }
    },
    async renameGuest (guest) {
      const name = window.prompt(this.$t('ce_guest_name'), guest.full_name)
      if (!name || !name.trim() || name.trim() === guest.full_name) return
      try {
        await renameClubEventGuest(this.detail.id, guest.id, name.trim())
        await this.refreshDetail()
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      }
    },
    async removeGuest (guest) {
      if (!window.confirm(this.$t('ce_remove_guest_confirm'))) return
      try {
        await deleteClubEventGuest(this.detail.id, guest.id)
        await this.refreshDetail()
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      }
    },
    async togglePayment () {
      this.paying = true
      try {
        const next = this.detail.payment_status === 'paid' ? 'pending' : 'paid'
        await setClubEventPayment(this.detail.id, next)
        await this.refreshDetail()
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.paying = false
      }
    },
    async confirmCancel () {
      this.canceling = true
      try {
        await cancelClubEvent(this.detail.id)
        this.cancelDialog = false
        await this.refreshDetail()
        this.notify(this.$t('ce_canceled_ok'))
      } catch (error) {
        this.notify(this.apiError(error), 'error')
      } finally {
        this.canceling = false
      }
    }
  }
}
</script>
